import styled from "styled-components";
import {
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
  ANIMATION,
} from "../../../../../../utils/constants";

export const NextPaymentCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  border-radius: 16px;
  box-shadow: var(--shadow-small);

  @media ${QUERIES.tabletLargeAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px;
  }

  @media ${QUERIES.laptopAndUp} {
    justify-content: center;
    padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
    border: none;
    border-left: 1px solid var(--color-card-border);
    border-radius: 0;
    box-shadow: none;
  }
`;

export const NextHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
  flex-wrap: wrap;
`;

export const NextLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-text-secondary);
`;

export const DueChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 999px;
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-primary);
  background-color: color-mix(in srgb, var(--color-primary) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--color-primary) 28%, transparent);
  white-space: nowrap;
`;

export const NextAmount = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-display-lg);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1;
  color: var(--color-text-default);
`;

export const SplitBar = styled.div`
  display: flex;
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  background-color: var(--color-border);
`;

export const SplitPrincipal = styled.div<{ $pct: number }>`
  width: ${({ $pct }) => `${$pct}%`};
  background-color: var(--color-primary);
`;

export const SplitLegend = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.md}px;
  flex-wrap: wrap;
`;

export const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  font-size: var(--text-base);
  color: var(--color-text-secondary);
`;

export const LegendSwatch = styled.span<{ $kind: "principal" | "interest" }>`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 3px;
  background-color: ${({ $kind }) =>
    $kind === "principal" ? "var(--color-primary)" : "var(--color-border)"};
`;

export const LegendValue = styled.span`
  font-weight: 600;
  color: var(--color-text-default);
`;

export const ButtonWrapper = styled.div`
  margin-top: ${SPACING.xs}px;
  text-align: center;

  button {
    width: 100%;
    transition: ${ANIMATION.transitions.all};

    &:hover:not(:disabled) {
      transform: translateY(-1px);
    }

    @media ${QUERIES.laptopAndUp} {
      width: 75%;
    }
  }
`;

export const NextFootnote = styled.p`
  margin: 0;
  text-align: center;
  font-size: var(--text-xs);
  font-style: italic;
  color: var(--color-text-secondary);
`;

export const EmptyScheduleText = styled.p`
  margin: 0;
  font-size: var(--text-base);
  color: var(--color-text-secondary);
`;

export const NeutralChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 999px;
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-text-muted);
  background-color: var(--color-gray-pill);
  border: 1px solid var(--color-card-border);
  white-space: nowrap;
`;

export const OverdueChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 999px;
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-warning);
  background-color: color-mix(in srgb, var(--color-warning) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--color-warning) 32%, transparent);
  white-space: nowrap;

  &::before {
    content: "";
    width: 0.4em;
    height: 0.4em;
    border-radius: 50%;
    background-color: currentColor;
  }
`;

export const EmptyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
`;

export const EmptyIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${SPACING.xxl}px;
  height: ${SPACING.xxl}px;
  border-radius: 12px;
  color: var(--color-text-secondary);
  background-color: var(--color-gray-pill);

  svg {
    font-size: var(--text-xxl);
  }
`;

export const EmptyTitle = styled.h2`
  margin: 0;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xxl);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: var(--color-text-default);
`;

export const EmptyBody = styled.p`
  margin: 0;
  font-size: var(--text-base);
  line-height: 1.5;
  color: var(--color-text-secondary);
`;

export const ApproxRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  flex-wrap: wrap;
`;

export const ApproxGlyph = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-display);
  font-weight: 700;
  line-height: 1;
  color: var(--color-text-secondary);
`;

export const EstBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 8px;
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  background-color: var(--color-gray-pill);
`;

export const EstimateNote = styled.p`
  margin: 0;
  font-size: var(--text-base);
  font-style: italic;
  line-height: 1.5;
  color: var(--color-text-secondary);
`;

export const OverdueNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.md}px;
  border-radius: 12px;
  background-color: var(--color-warning-background);
  border: 1px solid var(--color-warning-border);

  svg {
    flex: none;
    margin-top: ${SPACING.xxs}px;
    font-size: var(--text-xl);
    color: var(--color-warning);
  }
`;

export const OverdueNoticeText = styled.p`
  margin: 0;
  font-size: var(--text-base);
  line-height: 1.5;
  color: var(--color-text-default);

  strong {
    font-weight: 700;
  }
`;
